import React, { useState, useRef, useCallback } from "react";
import * as yup from "yup";
import PropTypes from "prop-types";
import InputMask from "react-input-mask";
import { Field, Form, Formik } from "formik";
import { FormContainer, CheckboxContainer } from "./styles";
import { trackClick, MARKETPLACE_TAG_IDS } from "../../utils/gtm";
import { isFixedAddress, isMainAddress } from "./addressUtils";

const ALLOWED_CHARS = /[a-zA-Z0-9\u00C0-\u024F\s.,\-/°ºª]/;

function sanitizeInput(value) {
  return value
    .split("")
    .filter((ch) => ALLOWED_CHARS.test(ch))
    .join("");
}

function FilteredField({ field, form, className, placeholder, maxLength, onBlur }) {
  const handleChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    form.setFieldValue(field.name, sanitized);
  };

  return (
    <input
      type="text"
      className={className}
      placeholder={placeholder}
      maxLength={maxLength}
      name={field.name}
      value={field.value}
      onChange={handleChange}
      onBlur={onBlur || field.onBlur}
    />
  );
}

FilteredField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
    onBlur: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  onBlur: PropTypes.func,
};

export default function FormAddress({ address, handleCancel, handleSubmit, highlightMissingFields }) {

  const [loadingCep, setLoadingCep] = useState(false);
  const debounceTimerRef = useRef(null);

  const trackAddressEvent = (title) => {
    const contentType =
      address && Object.keys(address).length > 0
        ? "alterar-endereco"
        : "cadastrar-novo-endereco";
    trackClick({
      title,
      screenName: "minha-conta",
      contentType,
      tagId: MARKETPLACE_TAG_IDS.clickAddressForm,
    });
  };

  const getAddressByCep = useCallback((cep, setFieldValue, setFieldError) => {
    setFieldValue("cep", cep);

    const clearValues = () => {
      setFieldValue("streetName", "");
      setFieldValue("city", "");
      setFieldValue("neighborhood", "");
      setFieldValue("state", "");
    };

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const cleanCep = cep.replace(/\D/g, "");

    if (cleanCep.length >= 8) {
      setLoadingCep(true);
      debounceTimerRef.current = setTimeout(() => {
        fetch(`https://viacep.com.br/ws/${cleanCep}/json`)
          .then((response) => response.json())
          .then((data) => {
            if (data.erro) {
              clearValues();
              setFieldError("cep", "error");
              return false;
            }

            setFieldError("cep", "");
            setFieldError("streetName", "");
            setFieldError("city", "");
            setFieldError("neighborhood", "");
            setFieldError("state", "");

            setFieldValue("streetName", data.logradouro);
            setFieldValue("city", data.localidade);
            setFieldValue("neighborhood", data.bairro);
            setFieldValue("state", data.uf);

            setLoadingCep(false);
          })
          .catch(() => {
            clearValues();
            setFieldError("cep", "erro");

            setLoadingCep(false);
          });
      }, 500);
    } else {
      setLoadingCep(false);
      if (cleanCep.length > 0) {
        setFieldError("cep", "CEP inválido");
      }
    }
  }, []);

  const hasAlphanumeric = (value) => /[a-zA-Z0-9\u00C0-\u024F]/.test(value || "");

  const textField = (required = true) => {
    let schema = yup
      .string()
      .transform((value) => (value ? value.trim() : value));

    if (required) {
      schema = schema
        .required()
        .test("has-alphanumeric", "Campo inválido", hasAlphanumeric);
    } else {
      schema = schema.test(
        "optional-alphanumeric",
        "Campo inválido",
        (value) => !value || hasAlphanumeric(value),
      );
    }

    return schema;
  };

  const validationSchema = yup.object({
    cep: yup.string().required(),
    recipient: textField(false),
    label: textField(true),
    streetName: textField(true),
    number: yup.number().typeError("Somente números").required(),
    complement: textField(false),
    neighborhood: textField(true),
    city: textField(true),
    state: textField(true),
    reference: textField(false),
    main: yup.boolean().notRequired(),
  });

  const isEditing = Boolean(address?.uuid);
  const isLocked = isFixedAddress(address);

  const initialValues = {
    recipient: address?.recipient ?? "",
    label: address?.label ?? "",
    cep: address?.zipCode ?? "",
    streetName: address?.streetName ?? "",
    number: address?.number ?? "",
    complement: address?.complement ?? "",
    neighborhood: address?.neighborhood ?? "",
    city: address?.city ?? "",
    state: address?.state ?? "",
    reference: address?.reference ?? "",
    main: isMainAddress(address),
    fixed: address?.fixed,
  };

  const missingFieldsErrors = {};
  const missingFieldsTouched = {};
  if (highlightMissingFields) {
    const requiredFields = [
      "cep",
      "label",
      "streetName",
      "number",
      "neighborhood",
      "city",
      "state",
    ];
    requiredFields.forEach((key) => {
      const value = initialValues[key];
      if (value === "" || value === null || value === undefined) {
        missingFieldsErrors[key] = "Campo obrigatório";
        missingFieldsTouched[key] = true;
      }
    });
  }

  return (
    <FormContainer>
      <Formik
        initialValues={initialValues}
        initialErrors={missingFieldsErrors}
        initialTouched={missingFieldsTouched}
        validationSchema={validationSchema}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, actions) => {
          try {
            const trimmedValues = Object.fromEntries(
              Object.entries(values).map(([key, val]) => [
                key,
                typeof val === "string" ? val.trim() : val,
              ]),
            );

            const formValues = {
              ...trimmedValues,
              content_type:
                Object.keys(address).length > 0
                  ? "alterar-endereco"
                  : "cadastrar-novo-endereco",
            };

            trackAddressEvent("adicionar Endereço");

            const response = await handleSubmit(formValues);

            !address.uuid && actions.resetForm();
          } catch (error) {
            console.error("Erro ao salvar:", error);
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          touched,
          setFieldValue,
          setFieldError,
          validateField,
          isSubmitting,
          values,
        }) => {
          return (
            <>
              <div className="mb-4 form-address">
                <h2 className="form-address__title font-wl-600 ">
                  {!isEditing
                    ? "Informar endereço"
                    : "Alterar dados do endereço"}
                </h2>
                {!isEditing && (
                  <>Para prosseguir com o pagamento, informe um endereço para a entrega da sua compra.</>
                )}
              </div>
              <Form className="form-address">
                <div className="mb-2">
                  <div className="input-group">
                    <Field name="label">
                      {({ field, form }) => (
                        <FilteredField
                          field={field}
                          form={form}
                          className={`input-custom form-address__input ${
                            errors.label && touched.label ? "is-invalid" : null
                          }`}
                          placeholder="Identificar este endereço como"
                          maxLength={30}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="mb-2">
                    <div className="input-group">
                      <Field name="cep">
                        {({ field }) => (
                          <div style={{ width: "100%" }}>
                            <InputMask
                              className={`input-custom form-address__input ${
                                errors.cep && touched.cep ? "is-invalid" : null
                              }`}
                              mask="99999-999"
                              maskChar={false}
                              type="tel"
                              inputMode="numeric"
                              {...field}
                              placeholder="CEP"
                              onChange={(e) =>
                                getAddressByCep(
                                  e.target.value,
                                  setFieldValue,
                                  setFieldError,
                                )
                              }
                            />
                            {errors.cep && touched.cep && (
                              <div className="invalid-feedback d-block italic">
                                cep inválido
                              </div>
                            )}
                          </div>
                        )}
                      </Field>

                      <span className="input-group-wl mt-1">
                        {loadingCep && (
                          <span
                            className="spinner-border spinner-border-xs"
                            role="status"
                          />
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="mb-2">
                    <Field name="streetName">
                      {({ field, form }) => (
                        <FilteredField
                          field={field}
                          form={form}
                          className={`input-custom form-address__input ${
                            errors.streetName && touched.streetName
                              ? "is-invalid"
                              : null
                          }`}
                          placeholder="Endereço"
                        />
                      )}
                    </Field>
                  </div>

                  <div className="row mb-2">
                    <div className="col-6">
                      <Field name="number">
                        {({ field, form }) => (
                          <FilteredField
                            field={field}
                            form={form}
                            className={`input-custom form-address__input ${
                              errors.number && touched.number ? "is-invalid" : null
                            }`}
                            placeholder="Número"
                            onBlur={() => validateField("number")}
                          />
                        )}
                      </Field>
                    </div>

                    <div className="col-6">
                      <Field name="complement">
                        {({ field, form }) => (
                          <FilteredField
                            field={field}
                            form={form}
                            className={`input-custom form-address__input ${
                              errors.complement && touched.complement
                                ? "is-invalid"
                                : null
                            }`}
                            placeholder="Complemento"
                            onBlur={() => validateField("complement")}
                          />
                        )}
                      </Field>
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-6">
                      <Field name="neighborhood">
                        {({ field, form }) => (
                          <FilteredField
                            field={field}
                            form={form}
                            className={`input-custom form-address__input ${
                              errors.neighborhood && touched.neighborhood
                                ? "is-invalid"
                                : null
                            }`}
                            placeholder="Bairro"
                          />
                        )}
                      </Field>
                    </div>
                    <div className="col-6">
                      <Field name="city">
                        {({ field, form }) => (
                          <FilteredField
                            field={field}
                            form={form}
                            className={`input-custom form-address__input ${
                              errors.city && touched.city ? "is-invalid" : null
                            }`}
                            placeholder="Cidade"
                          />
                        )}
                      </Field>
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-6">
                      <Field name="state">
                        {({ field, form }) => (
                          <FilteredField
                            field={field}
                            form={form}
                            className={`input-custom form-address__input ${
                              errors.state && touched.state ? "is-invalid" : null
                            }`}
                            placeholder="Estado"
                          />
                        )}
                      </Field>
                    </div>
                    <div className="col-6">
                      <Field name="reference">
                        {({ field, form }) => (
                          <FilteredField
                            field={field}
                            form={form}
                            className={`input-custom form-address__input ${
                              errors.reference && touched.reference
                                ? "is-invalid"
                                : null
                            }`}
                            placeholder="Referência"
                            onBlur={() => validateField("reference")}
                          />
                        )}
                      </Field>
                    </div>
                  </div>

                  {!isLocked && (
                    <CheckboxContainer className="form-address__checkbox my-3">
                      <Field name="main">
                        {({ field, form }) => (
                          <input
                            type="checkbox"
                            id="main-address"
                            checked={Boolean(field.value)}
                            onChange={() =>
                              form.setFieldValue("main", !field.value)
                            }
                          />
                        )}
                      </Field>
                      <label
                        htmlFor="main-address"
                        className="form-address__label mb-0"
                      >
                        Salvar como endereço principal
                      </label>
                    </CheckboxContainer>
                  )}
                </div>

                <div className="d-flex align-items-center  justify-content-center my-4">
                  <button
                    type="submit"
                    disabled={isSubmitting || isLocked}
                    className="btn-default-wl"
                    style={{
                      width: 187,
                      height: 36.45,
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {isSubmitting ? (
                      <span
                        className="spinner-border spinner-border-sm "
                        role="status"
                      />
                    ) : isEditing ? (
                      "SALVAR"
                    ) : (
                      "ADICIONAR"
                    )}
                  </button>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </FormContainer>
  );
}

FormAddress.propTypes = {
  address: PropTypes.any,
  handleCancel: PropTypes.func,
  handleSubmit: PropTypes.func,
  highlightMissingFields: PropTypes.bool,
};
